import React from 'react';
import PropTypes from 'prop-types';
import { useTranslation } from 'react-i18next';
import { Box, Text } from '@reservamos/elements';
import LocalizedCurrency from 'components/LocalizedCurrency';
import petBadge from 'images/badges/pet.svg';
import adjacentBadge from 'images/badges/adjacent-seat.svg';
import womanBadge from 'images/brands-unique/gfa/badge-woman.svg';
import wheelchairBadge from 'images/badges/wheelchair.svg';
import 'styles/components/SeatsLegend/NewSeatsLegend';
import NewSeatLegendItem from 'ui/atoms/NewSeatLegendItem';
import { buildZoneMap } from 'utils/seatPricing';
import { isSeatOccupied } from 'utils/purchase/seats';

/**
 * Check if the layout has a seat type
 * @param {Array} layout
 * @param {String|Function} check
 * @returns
 */
function hasSeatType(layout, check) {
  if (!layout) return false;

  return layout.some((row) => {
    return row.some((seats) => {
      return seats.some((seat) => {
        return typeof check === 'function' ? check(seat) : seat.category === check;
      });
    });
  });
}

/**
 * Component for rendering the seats legend.
 *
 * @param {Object} props - The component props.
 * @param {Array} props.layout - The layout of the seats.
 * @param {boolean} props.showWomanLegends - Whether to show woman legends.
 * @param {boolean} props.showPetFriendly - Whether to show pet-friendly legends.
 * @param {boolean} props.isHorizontal - Whether the layout is horizontal.
 * @param {number} props.occupied - The number of occupied seats.
 * @param {number} props.available - The number of available seats.
 * @param {number} props.selected - The number of selected seats.
 * @param {boolean} props.showRecommended - Whether to show recommended seats.
 * @param {boolean} props.isBoxMobile - Whether to use mobile box styling.
 * @param {boolean} props.showAdjacentSeatLegend - Whether to show adjacent seat legend.
 * @param {boolean} props.adjacentToggleActive - Whether the adjacent seat toggle is active.
 * @param {string} props.floor - The floor number.
 * @param {boolean} props.hasSecondFloor - Whether the bus has a second floor.
 */
function NewSeatsLegend({
  layout,
  showWomanLegends,
  showPetFriendly,
  isHorizontal,
  occupied,
  available,
  selected,
  showRecommended,
  isBoxMobile,
  showAdjacentSeatLegend,
  adjacentToggleActive,
  floor,
  hasSecondFloor,
  seatPriceZones,
  useSeatZoneLegend,
}) {
  const { t } = useTranslation('seats');

  const hasdisabledSeats = hasSeatType(layout, 'seat_disabled') || adjacentToggleActive;
  const hasHandicapSeat = hasSeatType(
    layout,
    (seat) => seat.category === 'seat_handicap' && !isSeatOccupied(seat),
  );

  const hasAdjacentSeats = hasSeatType(
    layout,
    (seat) => seat.adjacentSeats && !isSeatOccupied(seat),
  );

  const floorsLabel = floor === '2' ? t('seats:2nd_floor') : t('seats:1st_floor');
  const zoneMap = buildZoneMap({ seatPriceZones });
  const zoneLegendItems = Object.values(zoneMap).sort((a, b) => a.order - b.order);

  /**
   * Gets the appropriate header title based on legend and floor info.
   * @returns {string} The header title text.
   */
  const getHeaderTitle = () => {
    if (hasSecondFloor) return `${t('your_seats')} - ${floorsLabel}`;

    return t('your_seats');
  };

  return (
    <div
      className={`new-seats-legend-container ${
        isHorizontal ? 'new-seats-legend-horizontal' : 'new-seats-legend-box'
      } ${isBoxMobile ? 'new-seats-legend-box-mobile' : ''}`}
    >
      {!isHorizontal && (
        <Text xsMobileSize="XXS" mobileSize="XS" size="S" textAlign="center" weight="semibold">
          {getHeaderTitle()}
        </Text>
      )}

      {useSeatZoneLegend && zoneLegendItems.length > 0 ? (
        zoneLegendItems.map((zone) => (
          <div
            key={zone.zoneKey}
            className="new-seats-zone-legend-item"
            style={{
              minWidth: isHorizontal && !isBoxMobile ? 92 : undefined,
            }}
          >
            <div
              className="new-seats-zone-legend-color"
              style={{
                width: 18,
                height: 18,
                minWidth: 18,
                borderRadius: 4,
                backgroundColor: zone.color,
              }}
            />
            <div className="new-seats-zone-legend-texts">
              <LocalizedCurrency
                currencyStyle="currency"
                price={Number(zone.price)}
                size={isBoxMobile ? 'XXS' : 'XS'}
                weight="semibold"
                color="grayLight"
              />
              <Text
                size="XXS"
                weight="regular"
                color="grayLight"
                className="new-seats-zone-legend-label"
              >
                {zone.label}
              </Text>
            </div>
          </div>
        ))
      ) : (
        <>
          <NewSeatLegendItem
            text={t('seat', { context: 'available', count: available })}
            type="available"
            countSeats={available}
            isVertical={isHorizontal}
          />

          <NewSeatLegendItem
            text={t('seat', { context: 'selected', count: selected })}
            type="selected"
            countSeats={selected}
            isVertical={isHorizontal}
          />

          <NewSeatLegendItem
            text={t('seat', { context: 'occupied', count: occupied })}
            type="occupied"
            countSeats={occupied}
            isVertical={isHorizontal}
          />
        </>
      )}

      {(showRecommended || showPetFriendly || showWomanLegends || hasHandicapSeat) && (
        <Box borderColor="grayBorder" border={isHorizontal ? 'left' : 'bottom'} />
      )}

      {showRecommended && (
        <NewSeatLegendItem
          text={t('seat', { context: 'recommended' })}
          type="available"
          isVertical={isHorizontal}
          isRecommended
        />
      )}

      {showPetFriendly && (
        <NewSeatLegendItem
          text={t('passengers:type.pet_friendly')}
          iconType={petBadge}
          isVertical={isHorizontal}
        />
      )}

      {showAdjacentSeatLegend && hasAdjacentSeats && (
        <NewSeatLegendItem
          text={t('adjacent_seat.label')}
          iconType={adjacentBadge}
          isVertical={isHorizontal}
        />
      )}

      {showWomanLegends && (
        <NewSeatLegendItem
          text={t('seat', { context: 'woman' })}
          iconType={womanBadge}
          isVertical={isHorizontal}
        />
      )}

      {hasHandicapSeat && (
        <NewSeatLegendItem
          text={t('seat', { context: 'handicap' })}
          iconType={wheelchairBadge}
          isVertical={isHorizontal}
        />
      )}

      {hasdisabledSeats && (
        <NewSeatLegendItem text={t('seat_disabled')} type="disabled" isVertical={isHorizontal} />
      )}
    </div>
  );
}

NewSeatsLegend.propTypes = {
  layout: PropTypes.array,
  showWomanLegends: PropTypes.bool,
  showPetFriendly: PropTypes.bool,
  isHorizontal: PropTypes.bool,
  occupied: PropTypes.number,
  available: PropTypes.number,
  selected: PropTypes.number,
  showRecommended: PropTypes.bool,
  isBoxMobile: PropTypes.bool,
  showAdjacentSeatLegend: PropTypes.bool,
  adjacentToggleActive: PropTypes.bool,
  floor: PropTypes.string,
  hasSecondFloor: PropTypes.bool,
  seatPriceZones: PropTypes.arrayOf(
    PropTypes.shape({
      seatFareReference: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
      price: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
    }),
  ),
  useSeatZoneLegend: PropTypes.bool,
};

NewSeatsLegend.defaultProps = {
  layout: [],
  showWomanLegends: false,
  showPetFriendly: false,
  isHorizontal: false,
  occupied: 0,
  available: 0,
  selected: 0,
  showRecommended: false,
  isBoxMobile: false,
  showAdjacentSeatLegend: false,
  adjacentToggleActive: false,
  floor: '1',
  hasSecondFloor: false,
  seatPriceZones: [],
  useSeatZoneLegend: false,
};

export default NewSeatsLegend;
